/**
 * Prompt-action / feat-modifier icons (stroke-based, neutral white/grey): the
 * checkmark, cross, skat-cards, struck skat-cards (Hand), scissors
 * (Schneider/Schwarz), spectacles, cards-face-down, and ouvert fan. They mark
 * bidding choices and round modifiers; the meaning lives at the call site, the
 * factory is named for what it draws. See docs/components/ICONOGRAPHY.md
 * § Prompt-Action & Feat Icons; the button-context chrome this file also
 * carries (menu, Fullscreen, gate pair, eye pair, the overlay shell's close
 * saltire and its chevron, result-strip affordances) is
 * docs/components/MARKS.md.
 *
 * Colors are hardcoded (#F1F5F9 vellum primary, #94A3B8 slate secondary) —
 * same pattern as game type icons. The button-context icons are the
 * currentColor exceptions: createSpectacles, the eye pair (createEye /
 * createEyeStruck — the auth form's show-password toggle), the gate pair
 * (createGateEnter / createGateExit), the menu mark (createMenuIcon), the
 * Fullscreen mark (createBattlementCorners), and the overlay shell's chrome
 * pair (createSwallowtailSaltire / createPateeChevron); the "Your games" skat
 * cards (createSkatCards — normalized to currentColor, start-screen-unfolded-
 * menu-strip design, since it briefly joined the site-nav/menu-entry family; back
 * to meaning only "Pick up Skat" now that `icons/nav-icons.js#createLantern`
 * owns that nav entry — see below), and the result strip's History
 * (createAnnalsBook) / Tricks (createTrickTriangle) affordances also recolor
 * with their host.
 *
 * The site-nav / pause-menu-entry marks (Help, Rules, Strategy, Glossary,
 * Leaderboard, Multiplayer, Features, Your games) and the credential mark
 * live in the sibling `icons/nav-icons.js` now — split out (nav-icon-
 * candidate-placements task, 2026-08-17) once this file's own growth pushed
 * it over the JS module size budget (CODING.md § Size Limits); see that
 * file's own header for the SRP boundary the split follows.
 */
import { addArtisanMandalaPips } from "../cards/card-backs.js";
import { SVG_NS, svg, path, filledPath, filledDot } from "./svg.js";

/** Bidding accept icon: bold checkmark. */
export function createCheckmark(size) {
  const el = svg(size, size);
  const check = document.createElementNS(SVG_NS, "path");
  check.setAttribute("d", "M12 25 L20 33 L36 15");
  check.setAttribute("fill", "none");
  check.setAttribute("stroke", "#F1F5F9");
  check.setAttribute("stroke-width", "4.5");
  check.setAttribute("stroke-linecap", "round");
  check.setAttribute("stroke-linejoin", "round");
  el.appendChild(check);
  return el;
}

/** Bidding pass icon: bold X. */
export function createCross(size) {
  const el = svg(size, size);
  const cross = document.createElementNS(SVG_NS, "path");
  cross.setAttribute("d", "M14 14 L34 34 M34 14 L14 34");
  cross.setAttribute("fill", "none");
  cross.setAttribute("stroke", "#F1F5F9");
  cross.setAttribute("stroke-width", "4.5");
  cross.setAttribute("stroke-linecap", "round");
  cross.setAttribute("stroke-linejoin", "round");
  el.appendChild(cross);
  return el;
}

/** One face-down card of the skat pair: outlined rect, tilted ±6°. */
function skatCard(x, rotate) {
  const r = document.createElementNS(SVG_NS, "rect");
  r.setAttribute("x", x);
  r.setAttribute("y", "10");
  r.setAttribute("width", "18");
  r.setAttribute("height", "26");
  r.setAttribute("rx", "2.5");
  r.setAttribute("fill", "none");
  r.setAttribute("stroke", "currentColor");
  r.setAttribute("stroke-width", "2");
  r.setAttribute("transform", rotate);
  return r;
}

/** Pick up Skat icon: two overlapping face-down cards wearing the Artisan
 *  mandala arrangement, like the Verdeckt tile. Mandala on the front card
 *  only — with fill:none cards, pips on the back card would show through the
 *  overlap. currentColor (start-screen-unfolded-menu-strip design — joined the button-context
 *  family beside the site-nav/menu marks (`icons/nav-icons.js`) when it briefly doubled as the
 *  site-nav/menu Your-games entry): was hardcoded vellum `#F1F5F9`. Review correction: this does
 *  change rendered pixels in both of its real hosts — `.menu-overlay-entry`'s `color` isn't
 *  `#f1f5f9` (a dimmer `rgba(226,232,240,0.92)`) and the start-screen strip's tile host renders
 *  even brighter (see its own font-family fix's finding) — so this is a genuine ink change, not a
 *  no-op. The normalization is still the right call (it now matches its five siblings' currentColor
 *  behavior instead of standing apart), the claim about it being pixel-invisible was just wrong.
 *  createSkatCardsStruck (below) inherits the card body's currentColor through this factory but
 *  keeps its own strike hardcoded — same reasoning, same correction applies. The Your-games nav
 *  double-duty has since ended (nav-icon-candidate-placements task — `icons/nav-icons.js#
 *  createLantern` took the entry): this factory is back to meaning only "Pick up Skat", still
 *  currentColor regardless (Hand's strike above still needs it). */
export function createSkatCards(size) {
  const el = svg(size, size);
  el.appendChild(skatCard(9, "rotate(-6, 18, 23)"));
  el.appendChild(skatCard(21, "rotate(6, 30, 23)"));
  const pips = document.createElementNS(SVG_NS, "g");
  pips.setAttribute("transform", "rotate(6, 30, 23)");
  pips.setAttribute("opacity", "0.55");
  addArtisanMandalaPips(
    pips,
    // hRadius ≈ vRadius/φ, matching the real card back's golden mandala (DESIGN #15); 4 not
    // 4.02 so the emitted translate() coordinates stay float-clean.
    { centerX: 30, centerY: 23, vRadius: 6.5, hRadius: 4, pipScale: 0.26 },
    "currentColor",
  );
  el.appendChild(pips);
  return el;
}

/** Play Hand icon: the skat-cards icon struck through — "the Skat stays
 *  down". Mirrors createSkatCards exactly (same two cards, same mandala) plus
 *  the strike, so the two declaration choices read as a true pair: take it /
 *  don't take it. */
export function createSkatCardsStruck(size) {
  const el = createSkatCards(size);
  const strike = path("M8 39 L40 7", 2.5);
  strike.setAttribute("stroke", "#F1F5F9");
  el.appendChild(strike);
  return el;
}

// Scissors feat icons (open = Schneider, closed = Schwarz). The scissor state
// alone carries the scoring escalation — open "poised to cut" (opponent ≤30) →
// closed "shut out" (opponent took zero). No severed-thread detail (it muddied at
// chip size) and the −22° tilt is load-bearing: it breaks the vertical symmetry
// that otherwise reads anatomically. The Schneider/Schwarz meaning lives at each
// call site (stats-panel.html, games.html), like Hand/Ouvert.
// why: specs/2026-06/2026-06-08-schneider-schwarz-icons-design.md.
const SCISSORS_GEOMETRY = {
  open: {
    paths: [
      "M15 39 Q19 32 24 25 Q31 18 37 9",
      "M33 39 Q29 32 24 25 Q17 18 11 9",
    ],
    rings: [
      [15, 41],
      [33, 41],
    ],
  },
  closed: {
    paths: [
      "M19 39 Q21 32 24 25 Q27 15 24 4",
      "M29 39 Q27 32 24 25 Q21 15 24 4",
    ],
    rings: [
      [19, 41],
      [29, 41],
    ],
  },
};

/** Builds a scissors icon (`"open"` | `"closed"`) — two curved blade/handle paths
 *  crossing the pivot, two finger rings, and a pivot screw, tilted −22° as a pair. */
function scissors(size, state) {
  const el = svg(size, size);
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("transform", "rotate(-22 24 24)");
  const { paths, rings } = SCISSORS_GEOMETRY[state];
  for (const d of paths) {
    const blade = path(d, 2.6);
    blade.setAttribute("stroke", "#F1F5F9");
    g.appendChild(blade);
  }
  for (const [cx, cy] of rings) {
    const ring = document.createElementNS(SVG_NS, "circle");
    ring.setAttribute("cx", cx);
    ring.setAttribute("cy", cy);
    ring.setAttribute("r", 4.0);
    ring.setAttribute("stroke", "#F1F5F9");
    ring.setAttribute("stroke-width", 2.4);
    ring.setAttribute("fill", "none");
    g.appendChild(ring);
  }
  const screw = document.createElementNS(SVG_NS, "circle");
  screw.setAttribute("cx", 24);
  screw.setAttribute("cy", 25);
  screw.setAttribute("r", 1.7);
  screw.setAttribute("fill", "#F1F5F9");
  screw.setAttribute("stroke", "none");
  g.appendChild(screw);
  el.appendChild(g);
  return el;
}

/** Schneider feat icon: open scissors ("poised to cut"). */
export function createScissorsOpen(size) {
  return scissors(size, "open");
}

/** Schwarz feat icon: closed scissors ("the cut is done"). */
export function createScissorsClosed(size) {
  return scissors(size, "closed");
}

/**
 * Spectacles icon for the on-demand hint button. The hint reflects what the
 * AI (named Lothar after Andre's grandfather, who taught him the game)
 * would do — and Lothar wore glasses. Two round lenses, bridge, temple arms.
 * Stroke-based, recolors via currentColor. Joins the prompt-action icon family
 * (checkmark, cross, skat-cards), not the heritage role-icon family.
 * See docs/design/IDENTITY.md for the Lothar connection.
 */
export function createSpectacles(size) {
  const el = svg(size, size);
  const lens = (cx) => {
    const c = document.createElementNS(SVG_NS, "circle");
    c.setAttribute("cx", cx);
    c.setAttribute("cy", 28);
    c.setAttribute("r", 8);
    c.setAttribute("fill", "none");
    c.setAttribute("stroke", "currentColor");
    c.setAttribute("stroke-width", 2.5);
    return c;
  };
  el.appendChild(lens(15));
  el.appendChild(lens(33));
  // Bridge — tiny arch between the lenses
  el.appendChild(path("M 23 27 q 2 -2 2 0"));
  // Temple arms — short strokes going up-and-out to where the temples meet
  el.appendChild(path("M 8 23 L 4 18 M 40 23 L 44 18"));
  return el;
}

/**
 * Eye icon for the auth form's show-password toggle (show-password-eye-toggle task) — promoted
 * from the number-glyphs lab exhibit's Augen candidate (dev/number-glyphs-spike.js), which lost
 * that race precisely because "eye = looking" is its natural read: exactly what this toggle
 * wants. Almond contour + iris + pupil, in the position trio's organic body grammar (a second use
 * of anatomy in a vocabulary that otherwise draws objects — position-icons.js is the first).
 * Hidden state: the password is masked, clicking reveals it. `currentColor`, like the spectacles
 * above — the button context recolors it. Distinct from the spectacles' "ask what Lothar sees":
 * this eye answers "see what you typed", a different surface (an auth-form field toggle vs. the
 * in-game hint button) with no shared context where the two reads could collide.
 */
export function createEye(size) {
  const el = svg(size, size);
  el.appendChild(path("M5 24 Q24 7 43 24 Q24 41 5 24 Z", 2.5)); // almond contour
  const iris = document.createElementNS(SVG_NS, "circle");
  iris.setAttribute("cx", 24);
  iris.setAttribute("cy", 24);
  iris.setAttribute("r", 6);
  iris.setAttribute("stroke", "currentColor");
  iris.setAttribute("stroke-width", 2.2);
  iris.setAttribute("fill", "none");
  el.appendChild(iris);
  el.appendChild(filledDot(24, 24, 2.4)); // pupil
  return el;
}

/** Eye, struck: the toggle's shown state ("click to hide") — the standard eye/eye-slash pair.
 *  Mirrors createEye exactly plus the same diagonal strike createSkatCardsStruck uses, so both
 *  toggle states read as one family. */
export function createEyeStruck(size) {
  const el = createEye(size);
  el.appendChild(path("M8 39 L40 7", 2.5));
  return el;
}

/** The shared gatehouse silhouette for the play/quit gate pair below — the
 *  battlement outline only, no gate detail, rendered subtle so the
 *  full-strength arrow owns the icon (the arrow IS the read: a crenellated
 *  v1 with arch + portcullis detail buried it and turned to mush below 48px;
 *  the silhouette pair survives the ramp down to 16px). */
function subtleGatehouse() {
  const g = document.createElementNS(SVG_NS, "g");
  g.setAttribute("opacity", "0.45");
  g.appendChild(
    path(
      "M8 36 L8 18 L14 18 L14 10 L22 10 L22 18 L26 18 L26 10 L34 10 L34 18 L40 18 L40 36",
      2.3,
    ),
  );
  return g;
}

/** Play affordance: going INTO the gatehouse — subtle silhouette, big arrow
 *  whose head lands inside. Enter the fortress to play; mirror of
 *  createGateExit. currentColor, like the other button-context factories. */
export function createGateEnter(size) {
  const el = svg(size, size);
  el.appendChild(subtleGatehouse());
  el.appendChild(path("M3 30 L25 30", 3));
  el.appendChild(path("M19 23.5 L26.5 30 L19 36.5", 3));
  return el;
}

/** Quit affordance: going OUT of the gatehouse — same silhouette, the
 *  arrow's tail starts inside and its head exits past the wall. */
export function createGateExit(size) {
  const el = svg(size, size);
  el.appendChild(subtleGatehouse());
  el.appendChild(path("M18 30 L43 30", 3));
  el.appendChild(path("M37 23.5 L44.5 30 L37 36.5", 3));
  return el;
}

/** Menu affordance: a crenellated battlement crown over two wall courses — a
 *  three-row "menu" read carrying a castle nod (echoes the gatehouse, our central
 *  role icon). currentColor, like the gate pair — recolors with its host button. */
export function createMenuIcon(size) {
  const el = svg(size, size);
  // Crown: 3 merlons / 2 crenels, baseline y16, merlon tops y10, spanning x9–39.
  el.appendChild(
    path(
      "M9 16 L9 10 L15 10 L15 16 L21 16 L21 10 L27 10 L27 16 L33 16 L33 10 L39 10 L39 16",
      2.4,
    ),
  );
  el.appendChild(path("M9 26 L39 26", 2.6)); // wall courses below
  el.appendChild(path("M9 35 L39 35", 2.6));
  return el;
}

/** The in-board Fullscreen affordance: four battlement corner-brackets — the universal
 *  fullscreen "corner-bracket" read, crenellated into our language the same way
 *  createMenuIcon re-cut the hamburger. currentColor, like the menu mark beside it — the
 *  host button recolors it. Rejected alternates (Andre's pixel review, fullscreen-on-
 *  resumed-session task): plain undisguised brackets (the generic-app mark, DESIGN
 *  Principle 12 — no castle vocabulary at all); a toothed-bracket variant meant to degrade
 *  gracefully at small sizes, which read as a microchip; a raised-portcullis cut, which
 *  read as upload. See docs/components/MARKS.md § The Fullscreen mark. */
export function createBattlementCorners(size) {
  const el = svg(size, size);
  const w = 2.9;
  el.appendChild(path("M21 6 L11 6 L11 12 L6 12 L6 21", w));
  el.appendChild(path("M27 6 L37 6 L37 12 L42 12 L42 21", w));
  el.appendChild(path("M21 42 L11 42 L11 36 L6 36 L6 27", w));
  el.appendChild(path("M27 42 L37 42 L37 36 L42 36 L42 27", w));
  return el;
}

// --- the overlay chrome pair: the close saltire and its chevron -------------
//
// One filled pen, two marks (overlay-close-heritage-x-icon, 2026-09-05). Both
// are GENERATED rather than written out as literal path data: the saltire from
// one four-arm walk, which is what keeps it mirror-symmetric on both diagonals
// (the constant every cut of the drawing round had to hold — ICONOGRAPHY
// § How a drawing round is briefed), and the chevron from the same edge
// language on two arms instead of four. The five rejected × cuts and two
// rejected chevrons stay on the /icon-lab Proposals tab
// (`dev/close-mark-spike.js`), which imports these two back rather than
// redrawing them.

const A = Math.SQRT1_2;
const C = 24; // the viewBox centre both marks are built around

const fmt = ([x, y]) => `${x.toFixed(2)} ${y.toFixed(2)}`;
const lerp = (q, r, f) => [q[0] + (r[0] - q[0]) * f, q[1] + (r[1] - q[1]) * f];
const nudge = (q, d, m) => [q[0] + d[0] * m, q[1] + d[1] * m];

/** A point at `s` along an arm's axis and `t` across it, in viewBox space. */
const at = (u, s, p, t) => [C + u[0] * s + p[0] * t, C + u[1] * s + p[1] * t];

/** One side of one arm, corner to notch (or back). Both controls sit `waist`
 *  inside the arm; `shoulder` places them along the edge, and emitting them in
 *  reverse for the notch→corner leg is what keeps the two sides of a notch
 *  mirror images rather than one long sweep.
 *
 *  The two literals shape the cubic; neither is a knob to re-tune. A cubic's
 *  drawn edge falls well short of its control offset, so `waist * 1.35` pushes
 *  the controls out past the bow actually wanted — `waist` is the intended
 *  inset, 1.35 is the reach correction that delivers it. `shoulder * 0.45`
 *  places the second control at under half the first's distance along the edge:
 *  the two controls share one offset but not one position, and that asymmetry
 *  is what turns the edge near the terminal while leaving the rest of the shaft
 *  straight. Spacing them evenly bows the whole edge instead. */
function sideEdge(cnr, ntch, inward, { waist, shoulder }, fromNotch) {
  const c1 = nudge(lerp(cnr, ntch, 1 - shoulder), inward, waist * 1.35);
  const c2 = nudge(lerp(cnr, ntch, 1 - shoulder * 0.45), inward, waist * 1.35);
  return fromNotch
    ? ` C${fmt(c2)} ${fmt(c1)} ${fmt(cnr)}`
    : ` C${fmt(c1)} ${fmt(c2)} ${fmt(ntch)}`;
}

/** The saltire's four arms on the diagonals (clockwise from up-left), each with
 *  its own across-vector `p` and the axis `n` of the notch that follows it. */
const ARMS = [0, 1, 2, 3].map((i) => {
  const a = (-135 + i * 90) * (Math.PI / 180);
  const u = [Math.cos(a), Math.sin(a)];
  return {
    u,
    p: [-u[1], u[0]],
    n: [Math.cos(a + Math.PI / 4), Math.sin(a + Math.PI / 4)],
  };
});

/** The saltire's proportions, in viewBox units. `arm` is centre-to-terminal
 *  along the diagonal, `tip` the terminal's half-width, `notch` how far the
 *  outline dips back toward the centre between two arms — which is what sets
 *  the waist where the four arms merge. `waist` bows each side edge in toward
 *  its own arm's axis and `shoulder` says how close to the terminal that bow
 *  begins — 0.5 here, which keeps the shaft nearly straight and turns it only
 *  toward the terminal, so the swallowtail's own notch stays the thing the eye
 *  reads. `swallow` is the depth of that terminal notch — the banner cloth's cut
 *  (`role-icons.js#addSwallowtailCloth`, straight-cut there, so straight here).
 *
 *  why: the ink stays exactly 12–36 — a 24-unit square, half the viewBox — as
 *  long as `arm + tip` is 16.97, because the corner sits at 45°, so its offset
 *  from the centre is that sum times √½. That half is the number the CSS
 *  sizing divides by (skat.css § Result Overlays, `.overlay-close-btn svg`). */
const SALTIRE = {
  arm: 13.57,
  tip: 3.4,
  notch: 3.6,
  waist: 1.15,
  shoulder: 0.5,
  swallow: 1.1,
};

/** One closed outline for the saltire, walked arm → notch → arm clockwise from
 *  the up-left arm; each terminal cuts inward to its swallowtail notch on the
 *  way across, which is the one place the family motif lands. */
function saltirePath() {
  const { arm, tip, notch, swallow } = SALTIRE;
  const corner = (i, side) => at(ARMS[i].u, arm, ARMS[i].p, side * tip);
  const notchPt = (i) => [C + ARMS[i].n[0] * notch, C + ARMS[i].n[1] * notch];
  const edge = (cnr, ntch, inward, fromNotch) =>
    sideEdge(cnr, ntch, inward, SALTIRE, fromNotch);

  let d = `M${fmt(corner(0, -1))}`;
  for (let i = 0; i < 4; i++) {
    const j = (i + 1) % 4;
    const { u, p } = ARMS[i];
    d += ` L${fmt(at(u, arm - swallow, p, 0))} L${fmt(corner(i, 1))}`;
    d += edge(corner(i, 1), notchPt(i), [-p[0], -p[1]], false);
    d += edge(corner(j, -1), notchPt(i), ARMS[j].p, true);
  }
  return `${d} Z`;
}

/** The tapered V, as drawn pointing left (`‹`): up the outer edge to the upper
 *  tip, across it, back down the inner edge to the apex notch, then the whole
 *  thing mirrored about y=24 so the two arms can't drift apart. Ink spans
 *  x 14.55–31.07 by y 9.93–38.07 — 16.52 wide by 28.14 tall (a filled path, so
 *  geometry IS ink; no stroke spills past it), which is what the CSS mount
 *  sizes divide by: a square box lands 0.586 of its height as drawn chevron. */
function pateeChevronPath() {
  const mir = ([x, y]) => [x, 48 - y];
  const u = [A, -A];
  const p = [A, A]; // the V's inner side
  // why: these six numbers ARE the ink extent, and the extent is what three CSS rules divide by
  // (skat.css § Result Overlays, § Hints, § Help Page — the back chevron's 32px box most
  // directly, since it mounts this viewBox raw rather than framed). `prompt-icons.test.js` pins
  // the hull they produce for exactly that reason. 17 is the tip's base x-coordinate on the
  // horizontal axis and 16.5 the arm's length out along the 45° diagonal, which together put the
  // tips at the right edge; 3.4 is the terminal's half-width and is deliberately the same number
  // as `SALTIRE.tip`, which is what makes the two marks read as one pen rather than two at their
  // own sizes.
  const tipCentre = [17 + 16.5 * u[0], C + 16.5 * u[1]];
  const outer = nudge(tipCentre, p, -3.4);
  const inner = nudge(tipCentre, p, 3.4);
  // why: the apex, as three points — the inner edges meet the axis at 20.4, the outer corner sits
  // at 14.9/22.6, and the nose is rounded by a control at x 14.2 (below, off-curve, so the drawn
  // ink starts at 14.55 rather than there). The 20.4-to-14.2 gap is the mark's thickness at the
  // point, and the one place the V could read as blunt if it narrowed.
  const apexIn = [20.4, C];
  const apexUp = [14.9, 22.6];
  // why: `waist` 1.0 against the saltire's 1.15 — two arms carry a shallower taper than four
  // before the shaft looks pinched; `shoulder` 0.5 is the saltire's own, so both marks turn their
  // edges at the same point along the arm. `sideEdge` above explains what the pair does.
  const knobs = { waist: 1.0, shoulder: 0.5 };
  const edge = (cnr, end, inward, fromEnd) =>
    sideEdge(cnr, end, inward, knobs, fromEnd);
  // why: 1.6 bows each terminal edge out past the straight line between its two corners — the
  // swell that makes the tip read as cut rather than sawn. It rides the arm's own axis, but it
  // does NOT set the extent: its control lands at x 29.80, inside the 31.07 the `inner` corner
  // already reaches, so the hull is the corners' and this number is free to re-tune.
  const tipEdge = (centre, dir) => ` Q${fmt(nudge(centre, dir, 1.6))} `;
  return (
    `M${fmt(apexUp)}` +
    edge(outer, apexUp, p, true) +
    tipEdge(tipCentre, u) +
    fmt(inner) +
    edge(inner, apexIn, [-p[0], -p[1]], false) +
    edge(mir(inner), apexIn, [-p[0], p[1]], true) +
    tipEdge(mir(tipCentre), [u[0], -u[1]]) +
    fmt(mir(outer)) +
    edge(mir(outer), mir(apexUp), [p[0], -p[1]], false) +
    ` Q14.2 ${C} ${fmt(apexUp)} Z`
  );
}

/** The overlay's close mark: four arms ending in the banner cloth's own notch,
 *  so the terminals read as pennants radiating from one crossing — the × every-
 *  one knows, re-cut in the heritage vocabulary the same move `createMenuIcon`
 *  above made with the hamburger. currentColor, like the menu mark and the
 *  Fullscreen corners — the shell's chrome button recolors it. Held apart from
 *  `createCross` (the Pass tile's bold straight vellum X at stroke 4.5, which
 *  can share a screen with an open pause menu) by register: this one is filled
 *  and curved and carries a terminal treatment Pass has none of. Home:
 *  `overlay-shell.js#mountCloseButton`. See docs/components/MARKS.md
 *  § The overlay close mark and its chevron. */
export function createSwallowtailSaltire(size) {
  const el = svg(size, size);
  el.appendChild(filledPath(saltirePath()));
  return el;
}

/** The close mark's chevron pair, same filled pen: a tapered V — rounded at the
 *  apex, waisted along the shafts, flaring to swelled ends. At the ~13px of ink
 *  its homes give it the slot carries weight, not motif; the taper survives the
 *  ramp down, the terminal flare does not.
 *
 *  `direction` picks which way it points — "left" (the drawn orientation) is
 *  the overlay title row's back chevron, "right" the hint float's forward twin.
 *  The mirror lives here rather than at the call sites because `‹` and `›` are
 *  one mark seen twice: two of the three homes want the forward one, and none
 *  of the three (`overlay-shell.js`, `hints.js`, `help-enhance.js`) carries any
 *  other SVG geometry of its own. */
export function createPateeChevron(size, direction = "left") {
  const el = svg(size, size);
  const mark = filledPath(pateeChevronPath());
  if (direction === "right")
    mark.setAttribute("transform", "translate(48 0) scale(-1 1)");
  el.appendChild(mark);
  return el;
}

/** Result-strip History affordance: a bound ledger, open — two pages splayed
 *  from a shared spine, ruled entry lines standing in for the run's recorded
 *  rounds. Heritage-craft object like the chalice and strongbox; the Help
 *  scroll stays single-meaning (ICONOGRAPHY's no-overload precedent), so this
 *  is a bespoke cut rather than a scroll re-skin (see the rejected scroll-reuse
 *  alternate on the /icon-lab Proposals tab). currentColor, like the gate
 *  pair, menu mark, and Help scroll above — the result strip's glass tile
 *  recolors it. See docs/components/MARKS.md § The result-strip affordances. */
export function createAnnalsBook(size) {
  const el = svg(size, size);
  // Two pages splayed open from a shared spine — outer top corners lift,
  // outer bottom corners settle, so the silhouette alone reads "open book".
  // Drawn to fill the 48-box's ~34-unit height band the balance scale and
  // trick triangle occupy, so the four result-strip tiles read as one height.
  el.appendChild(path("M24 11.5 Q12 7 3 8 L3 40 Q12 41 24 36.5", 2.2));
  el.appendChild(path("M24 11.5 Q36 7 45 8 L45 40 Q36 41 24 36.5", 2.2));
  el.appendChild(path("M24 9.5 L24 39", 2)); // the spine binding
  // Ruled entry lines — the recorded rounds, one page per half.
  for (const y of [16.5, 23, 29]) {
    el.appendChild(path(`M7 ${y} L19 ${y}`, 1.6));
    el.appendChild(path(`M29 ${y} L41 ${y}`, 1.6));
  }
  return el;
}

// Trick-triangle geometry — three card slots the way three played cards settle
// in the trick area: a leading card up top, the other two overlapping in
// beneath it as they follow. Each rotates about its own center (skatCard()'s
// pivot convention above), not the tile origin.
const TRICK_TRIANGLE_CARDS = [
  { x: 17, y: 5, rotate: -5 },
  { x: 6, y: 21, rotate: -15 },
  { x: 28, y: 21, rotate: 15 },
];
const TRICK_CARD_W = 14;
const TRICK_CARD_H = 19;

/** Result-strip Tricks affordance: three drawn card outlines settled in the
 *  trick area's triangle arrangement — one per player, literal ("the round,
 *  trick by trick") and unmistakably ours (a magnifier over a card was
 *  exhibited and rejected as the generic-app failure mode, DESIGN Principle
 *  12 — see the /icon-lab Proposals tab). Completes the drawn-card-outline
 *  grammar createOuvertFan established: a corner index mark reads each card
 *  as a face showing, not a blank back. currentColor, like the History mark
 *  above. */
export function createTrickTriangle(size) {
  const el = svg(size, size);
  for (const { x, y, rotate } of TRICK_TRIANGLE_CARDS) {
    const cx = x + TRICK_CARD_W / 2;
    const cy = y + TRICK_CARD_H / 2;
    const g = document.createElementNS(SVG_NS, "g");
    g.setAttribute("transform", `rotate(${rotate}, ${cx}, ${cy})`);
    const card = document.createElementNS(SVG_NS, "rect");
    card.setAttribute("x", x);
    card.setAttribute("y", y);
    card.setAttribute("width", TRICK_CARD_W);
    card.setAttribute("height", TRICK_CARD_H);
    card.setAttribute("rx", "2");
    card.setAttribute("fill", "none");
    card.setAttribute("stroke", "currentColor");
    card.setAttribute("stroke-width", "2");
    g.appendChild(card);
    const mark = document.createElementNS(SVG_NS, "circle");
    mark.setAttribute("cx", x + 3.5);
    mark.setAttribute("cy", y + 4);
    mark.setAttribute("r", "1.3");
    mark.setAttribute("fill", "currentColor");
    mark.setAttribute("stroke", "none");
    g.appendChild(mark);
    el.appendChild(g);
  }
  return el;
}

// Counting-frame geometry — three rods with the beads mid-count: an asymmetric
// left/right split per rod so it reads as a reckoning in progress, not an ornament.
const COUNTING_RODS = [
  { y: 16, left: [13.5, 19.5], right: [35] },
  { y: 24, left: [13.5], right: [29, 35] },
  { y: 32, left: [13.5, 19.5, 25.5], right: [35] },
];

/** Result-strip Stats affordance: the merchant's counting frame (abacus) —
 *  your run's numbers, counted. Joins the merchant vocabulary the bid weight,
 *  strongbox, and balance scale established, and frees the balance scale to be
 *  purely the Bilanz/money mark (the strip's Net chip sits two tiles away
 *  wearing one — the double-scale read this cut resolves; Andre's pixel
 *  review). A bar chart was the obvious alternative and stays rejected — the
 *  generic-app "analytics" mark, DESIGN Principle 12's failure mode.
 *  Since grown into the general rounds-played mark: also the stats-v2 panel's
 *  rounds token (stats-panel-enhance.js) and the game-start leaderboard's
 *  Rounds header mark (leaderboard-header-show-dont-tell).
 *  currentColor, like the History and Tricks marks above. */
export function createCountingFrame(size) {
  const el = svg(size, size);
  const frame = document.createElementNS(SVG_NS, "rect");
  frame.setAttribute("x", "6.5");
  frame.setAttribute("y", "7.5");
  frame.setAttribute("width", "35");
  frame.setAttribute("height", "33");
  frame.setAttribute("rx", "2");
  frame.setAttribute("fill", "none");
  frame.setAttribute("stroke", "currentColor");
  frame.setAttribute("stroke-width", "2.4");
  el.appendChild(frame);
  for (const { y, left, right } of COUNTING_RODS) {
    el.appendChild(path(`M9 ${y} L39 ${y}`, 1.6));
    for (const x of [...left, ...right]) el.appendChild(filledDot(x, y, 2.7));
  }
  return el;
}

/** Verdeckt (face-down) prompt tile: a vellum card with the Artisan mandala
 *  arrangement (diamonds N, hearts S, spades W, clubs E) in faded vellum. */
export function createCardsFaceDown(size) {
  const el = svg(size, size, "0 0 48 48");
  // Card outline (centered ~26×34, rx=2.5)
  const card = document.createElementNS(SVG_NS, "rect");
  card.setAttribute("x", "11");
  card.setAttribute("y", "7");
  card.setAttribute("width", "26");
  card.setAttribute("height", "34");
  card.setAttribute("rx", "2.5");
  card.setAttribute("fill", "rgba(255, 255, 255, 0.04)");
  card.setAttribute("stroke", "#94A3B8");
  card.setAttribute("stroke-width", "1.5");
  el.appendChild(card);

  const pips = document.createElementNS(SVG_NS, "g");
  pips.setAttribute("fill", "rgba(226, 232, 240, 0.55)");
  addArtisanMandalaPips(
    pips,
    // hRadius = vRadius/φ, matching the real card back's golden mandala (DESIGN #15).
    { centerX: 24, centerY: 24, vRadius: 9, hRadius: 5.56, pipScale: 0.36 },
    "rgba(226, 232, 240, 0.55)",
  );
  el.appendChild(pips);
  return el;
}

/** Ouvert (face-up) prompt tile: a ±24° fan of three drawn card outlines with
 *  corner-index marks — face-up as a drawing, not a photograph. Completes the
 *  family grammar with createSkatCards: blank rects = face-down, marked rects
 *  = faces showing. (Replaced an earlier fan of three real composed cards —
 *  the family's one non-SVG factory and its one photograph among drawings.) */
export function createOuvertFan(size) {
  const el = svg(size, size);
  for (const angle of [-24, 0, 24]) {
    const g = document.createElementNS(SVG_NS, "g");
    // Side cards shift outward before rotating — outline-only cards have no
    // occlusion, so without the spread their edges cross into visual noise.
    const shift = Math.sign(angle) * 3.5;
    g.setAttribute(
      "transform",
      `translate(${shift}, 0) rotate(${angle}, 24, 38)`,
    );
    const card = document.createElementNS(SVG_NS, "rect");
    card.setAttribute("x", "17");
    card.setAttribute("y", "9");
    card.setAttribute("width", "14");
    card.setAttribute("height", "21");
    card.setAttribute("rx", "2");
    card.setAttribute("fill", "none");
    card.setAttribute("stroke", "#F1F5F9");
    card.setAttribute("stroke-width", "2");
    g.appendChild(card);
    // Corner index mark — the "this face is visible" cue
    const mark = document.createElementNS(SVG_NS, "circle");
    mark.setAttribute("cx", "20.5");
    mark.setAttribute("cy", "13");
    mark.setAttribute("r", "1.3");
    mark.setAttribute("fill", "#F1F5F9");
    mark.setAttribute("stroke", "none");
    g.appendChild(mark);
    el.appendChild(g);
  }
  return el;
}
